import { posthog } from 'posthog-js';
import { getPostHogInitConfig, getPostHogFallbackConfig } from '~/lib/config/posthog';

export class PostHogManager {
  private static instance: PostHogManager;
  private isInitialized = false;
  private useProxy = true;
  private fallbackAttempted = false;

  private constructor() {
    // Private constructor for singleton pattern
  }

  static getInstance(): PostHogManager {
    if (!PostHogManager.instance) {
      PostHogManager.instance = new PostHogManager();
    }

    return PostHogManager.instance;
  }

  async initialize(posthogKey: string): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    // Check environment variable to determine if proxy should be used
    const env = (window as any).ENV;
    const useProxyFromEnv = env?.POSTHOG_USE_PROXY;

    /*
     * Set useProxy based on environment variable
     * If POSTHOG_USE_PROXY is explicitly "false", don't use proxy
     * If it's "true" or undefined, use proxy (default behavior)
     */
    this.useProxy = useProxyFromEnv !== 'false';

    console.log('[PostHog Manager] ===== Environment Debug =====');
    console.log('[PostHog Manager] Raw ENV object:', env);
    console.log('[PostHog Manager] POSTHOG_USE_PROXY value:', useProxyFromEnv);
    console.log('[PostHog Manager] POSTHOG_USE_PROXY type:', typeof useProxyFromEnv);
    console.log('[PostHog Manager] Will use proxy:', this.useProxy);
    console.log('[PostHog Manager] ================================');

    try {
      // First attempt: Try with reverse proxy (if enabled)
      if (this.useProxy) {
        console.log('[PostHog Manager] Attempting to initialize with reverse proxy...');

        const proxyConfig = getPostHogInitConfig();

        // Test the proxy before initializing PostHog
        const proxyTest = await this.testProxy();

        if (proxyTest.success) {
          console.log('[PostHog Manager] Proxy test successful, initializing with proxy...');

          try {
            posthog.init(posthogKey, proxyConfig);

            // Add error monitoring for PostHog after initialization
            this.monitorPostHogErrors();

            this.isInitialized = true;
            console.log('[PostHog Manager] PostHog initialized successfully with reverse proxy');
            return true;
          } catch (initError) {
            console.error('[PostHog Manager] Failed to initialize PostHog with proxy config:', initError);
            console.log('[PostHog Manager] Falling back to direct connection...');
            this.useProxy = false;
          }
        } else {
          console.warn('[PostHog Manager] Proxy test failed:', proxyTest.error);
          console.log('[PostHog Manager] Will fallback to direct connection');
          this.useProxy = false;
        }
      }

      // Fallback: Use direct PostHog connection
      if (!this.fallbackAttempted) {
        const reason = this.useProxy
          ? 'Fallback after proxy failure'
          : 'Direct connection (proxy disabled by POSTHOG_USE_PROXY)';
        console.log(`[PostHog Manager] Initializing with direct PostHog connection... (${reason})`);

        const fallbackConfig = getPostHogFallbackConfig();
        posthog.init(posthogKey, fallbackConfig);
        this.isInitialized = true;
        this.fallbackAttempted = true;
        console.log('[PostHog Manager] PostHog initialized successfully with direct connection');
        return true;
      }

      return false;
    } catch (error) {
      console.error('[PostHog Manager] Failed to initialize PostHog:', error);

      // If proxy failed and we haven't tried fallback yet, try direct connection
      if (this.useProxy && !this.fallbackAttempted) {
        console.log('[PostHog Manager] Attempting fallback to direct connection...');
        this.useProxy = false;
        return this.initialize(posthogKey);
      }

      return false;
    }
  }

  private async testProxy(): Promise<{ success: boolean; error?: string }> {
    try {
      const posthogKey = (window as any).ENV?.POSTHOG_KEY || '';

      if (!posthogKey) {
        return { success: false, error: 'Missing PostHog key' };
      }

      // Test multiple endpoints that PostHog commonly uses
      const testEndpoints = [
        {
          path: 'decide',
          method: 'POST',
          body: JSON.stringify({
            token: posthogKey,
            distinct_id: 'proxy_test_' + Date.now(),
            groups: {},
          }),
        },
        {
          path: `array/${posthogKey}/config`,
          method: 'GET',
          body: null,
        },
      ];

      for (const endpoint of testEndpoints) {
        const proxyUrl = `${window.location.origin}/api/posthog/${endpoint.path}`;

        console.log(`[PostHog Manager] Testing proxy endpoint: ${proxyUrl}`);

        const response = await fetch(proxyUrl, {
          method: endpoint.method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: endpoint.body,
        });

        console.log(`[PostHog Manager] Proxy test response for ${endpoint.path}:`, {
          status: response.status,
          headers: Object.fromEntries(response.headers.entries()),
        });

        // Check if we're getting HTML (404 page) instead of JSON/PostHog response
        const contentType = response.headers.get('content-type') || '';
        const responseText = await response.text();

        if (contentType.includes('text/html') || responseText.includes('<!DOCTYPE html>')) {
          console.error(`[PostHog Manager] Proxy test failed - got HTML response for ${endpoint.path}`);
          return {
            success: false,
            error: `Proxy returning HTML instead of proxying to PostHog for ${endpoint.path}`,
          };
        }

        /*
         * Check for proxy errors
         */
        if (response.status === 502 || response.status === 503 || response.status === 504) {
          return { success: false, error: `Proxy server error: ${response.status}` };
        }

        if (response.headers.get('X-PostHog-Proxy-Error')) {
          return { success: false, error: 'Proxy configuration error' };
        }

        /*
         * If we get a 404 but it's not HTML, it might be a valid PostHog 404
         * If we get other errors, the proxy might still be working
         */
        if (response.status === 404 && !contentType.includes('text/html')) {
          // This is likely a valid PostHog 404, proxy is working
          continue;
        }
      }

      return { success: true };
    } catch (error) {
      console.error('[PostHog Manager] Proxy test failed with error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Method to manually switch to direct connection
  switchToDirect(): void {
    if (this.isInitialized && this.useProxy) {
      console.log('[PostHog Manager] Manually switching to direct connection...');
      this.useProxy = false;
      this.reinitialize();
    }
  }

  // Method to manually switch back to proxy
  switchToProxy(): void {
    // Check if proxy is allowed by environment variable
    const env = (window as any).ENV;
    const useProxyFromEnv = env?.POSTHOG_USE_PROXY;

    if (useProxyFromEnv === 'false') {
      console.warn('[PostHog Manager] Cannot switch to proxy - POSTHOG_USE_PROXY is set to false');
      return;
    }

    if (this.isInitialized && !this.useProxy) {
      console.log('[PostHog Manager] Manually switching to proxy...');
      this.useProxy = true;
      this.reinitialize();
    }
  }

  private reinitialize(): void {
    if (this.isInitialized) {
      const posthogKey = (window as any).ENV?.POSTHOG_KEY;

      if (posthogKey) {
        posthog.reset();
        this.isInitialized = false;
        this.fallbackAttempted = false;
        this.initialize(posthogKey);
      }
    }
  }

  // Get current connection status
  getConnectionStatus(): { isInitialized: boolean; useProxy: boolean; fallbackAttempted: boolean } {
    return {
      isInitialized: this.isInitialized,
      useProxy: this.useProxy,
      fallbackAttempted: this.fallbackAttempted,
    };
  }

  // Check if proxy is working
  async checkProxyHealth(): Promise<boolean> {
    const result = await this.testProxy();
    return result.success;
  }

  // Monitor PostHog errors and automatically fallback if needed
  private monitorPostHogErrors(): void {
    let errorCount = 0;
    const maxErrors = 3;
    const errorTimeWindow = 30000; // 30 seconds
    let errorTimestamp = Date.now();

    // Override console.error temporarily to catch PostHog errors
    const originalError = console.error;

    console.error = (...args: any[]) => {
      const message = args.join(' ');

      // Check for PostHog-specific errors that indicate proxy issues
      if (
        message.includes('[PostHog.js]') &&
        (message.includes('Bad HTTP status: 404') ||
          message.includes('<!DOCTYPE html>') ||
          message.includes('Failed to fetch') ||
          message.includes('could not rate limit'))
      ) {
        errorCount++;
        const now = Date.now();

        // Reset counter if errors are outside the time window
        if (now - errorTimestamp > errorTimeWindow) {
          errorCount = 1;
          errorTimestamp = now;
        }

        console.log(`[PostHog Manager] Detected PostHog error (${errorCount}/${maxErrors}):`, message);

        // If we hit too many errors, switch to direct connection (only if proxy was enabled)
        if (errorCount >= maxErrors && this.useProxy && this.isInitialized) {
          const env = (window as any).ENV;
          const useProxyFromEnv = env?.POSTHOG_USE_PROXY;

          // Only auto-switch if proxy wasn't explicitly enabled by environment variable
          if (useProxyFromEnv !== 'true') {
            console.warn('[PostHog Manager] Too many PostHog errors detected, switching to direct connection');
            this.switchToDirect();
          } else {
            console.warn(
              '[PostHog Manager] Too many PostHog errors detected, but POSTHOG_USE_PROXY is explicitly true - keeping proxy',
            );
          }
        }
      }

      // Call original console.error
      originalError.apply(console, args);
    };
  }
}

// Export singleton instance
export const posthogManager = PostHogManager.getInstance();
